import {Paginated, PaginatedResult} from "../models/default/paginated-result";
import {AxiosResponse} from "axios";
import {trackPromise} from "react-promise-tracker";
import Api from "./api-config";
import {Dictionary} from "./base-service";
import download from "downloadjs";

export type Filters = {
    [key: string]: any;
};

export type Expands = string[];

export class AbstractService<T> {
    path: string;

    constructor(path: string) {
        this.path = path;
    }

    async create(instance: T, filters?: Filters, expands?: Expands): Promise<AxiosResponse<T>> {
        const allParams = this.processParameters(filters, expands);
        return await trackPromise(Api.post<T>(this.path, instance, {params: allParams}));
    }

    async createFormData(formData: FormData, filters?: Filters, expands?: Expands): Promise<AxiosResponse<T>> {
        const allParams = this.processParameters(filters, expands);
        return await trackPromise(Api.post<T>(this.path, formData, {params: allParams}));
    }

    async readPaginated(filters?: Filters, expands?: Expands, pagination?: Paginated): Promise<AxiosResponse<PaginatedResult<T>>> {
        let path = this.path;

        const allParams = this.processParameters(filters, expands, pagination);

        return await trackPromise(Api.get(path, {params: allParams} ));
    }

    async readAll(filters?: Filters, expands?: Expands): Promise<AxiosResponse<T[]>> {
        let path = this.path;
        const allParams = this.processParameters(filters, expands);
        return await trackPromise(Api.get(path, {params: allParams}));
    }

    async read(filters?: Filters, expands?: Expands): Promise<AxiosResponse<T>> {
        const allParams = this.processParameters(filters, expands);
        return await trackPromise(Api.get<T>(this.path, {params: allParams}));
    }

    async patch(id: number, payload: Dictionary, filters?: Filters, expands?: Expands): Promise<AxiosResponse<T>> {
        const allParams = this.processParameters(filters, expands);
        return await trackPromise(Api.patch<T>(`${this.path}${id}/`, payload, {params: allParams}));
    }

    async delete(id: string | number, filters?: Filters, expands?: Expands): Promise<AxiosResponse<T | undefined>> {
        const allParams = this.processParameters(filters, expands);
        return await trackPromise(Api.delete<T | undefined>(`${this.path}${id}/`, {params: allParams}));
    }

    async readById(id: string | number, filters?: Filters, expands?: Expands): Promise<AxiosResponse<T>> {
        const allParams = this.processParameters(filters, expands);
        return await trackPromise(Api.get<T>(`${this.path}${id}/`, {params: allParams}));
    }

    async download(filters?: Filters, path_extension?: string): Promise<AxiosResponse<Blob>> {
        const allParams = this.processParameters(filters);
        return await trackPromise(Api.get<any>(`${this.path}${path_extension}`, {
          params: allParams,
          responseType: 'blob'
        }));
    }
  
    async downloadWithExpands(filters?: Filters, expands?: Expands, path_extension?: string): Promise<AxiosResponse<Blob>> {
      const allParams = this.processParameters(filters, expands);
      return await trackPromise(Api.get<any>(`${this.path}${path_extension}`, {
          params: allParams,
          responseType: 'blob'
      }));
    }

    protected processParameters = (filters?: Filters, expands?: Expands, pagination?: Paginated) => {
        const processesFilters = this.processFilters(filters);
        return {
            ...processesFilters,
            ...pagination,
            expand: expands?.join(","),
        }
    }

    protected processFilters = (filters?: Filters) : Filters | undefined => {
        return filters;
    }

    public downloadFile(blob: any) {
        if (blob) {
            download(
                new Blob([blob.data], {type: blob.data.type}),
                blob.headers['content-disposition'].match(/filename="(.+)"/)[1],
            );
        }
    }

    public getFormData = (obj: any) => {
        const formData = new FormData();
        Object.keys(obj).forEach(key => {
            if (obj[key] !== undefined) {
                formData.append(key, obj[key]);
            }
        });
        return formData
    }

}
